import React, { useState, useEffect } from "react";
import Cookies from "universal-cookie";
import { 
  MdRefresh, 
  MdLogout, 
  MdInfoOutline, 
  MdLockOutline,
  MdCheckCircle,
  MdArrowForward
} from "react-icons/md";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Dialog } from "@mui/material";
import { 
  COMPANYNAME, 
  NAME, 
  REDIRECT_URL, 
  ROLE, 
  SERVICE_KEY, 
  TOKEN_NAME, 
  USERNAME 
} from "../../config";
import { useQueryClient } from "@tanstack/react-query";
import { useOnboardingContext } from "../../context/OnboardingContext";
import toast from "react-hot-toast";

const cookies = new Cookies();

export default function EmployeePendingOnboarding() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showRestrictedModal, setShowRestrictedModal] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const queryClient = useQueryClient();
  const { companyCompletionStatus } = useOnboardingContext();
  const isCompleted = companyCompletionStatus === true;

  const companyName = cookies.get(COMPANYNAME) || "Your Company";
  const userName = cookies.get(NAME) || "Team Member";

  useEffect(() => {
    if (searchParams.get("restricted") === "true") {
      setShowRestrictedModal(true);
    }
  }, [searchParams]);

  const handleCloseRestrictedModal = () => {
    setShowRestrictedModal(false);
    searchParams.delete("restricted");
    setSearchParams(searchParams, { replace: true });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries(["is_permission_for_product"]);
      toast.success("Checked organization setup status");
    } catch (error) {
      console.error("Error refreshing status:", error);
    } finally {
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500);
    }
  };

  const handleLogout = () => {
    cookies.remove(TOKEN_NAME);
    cookies.remove(ROLE);
    cookies.remove(USERNAME);
    window.location.href = `${REDIRECT_URL}?pid=${SERVICE_KEY}`;
  };

  return (
    <div className="bg-[#f9fafb] min-h-screen w-full flex items-start justify-center p-4 sm:p-8 pt-8 sm:pt-12">
      <div className="bg-white border border-[#f1f5f9] drop-shadow-[0px_1px_1px_rgba(0,0,0,0.05)] rounded-[16px] w-full max-w-[1000px] p-6 lg:p-[40px] pb-10 relative box-border">
        
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6 pb-6 border-b border-[#f1f5f9] w-full">
          <div className="flex flex-col gap-2 flex-1">
            <h1 className="font-bold text-[#0f172a] text-[26px] sm:text-[30px] leading-[36px] m-0">
              Welcome, {userName}
            </h1>
            <p className="font-normal text-[#64748b] text-[15px] sm:text-[16px] leading-[24px] m-0">
              {isCompleted ? (
                <>
                  <span className="font-semibold text-[#334155]">{companyName}</span> has completed the organization setup on HRMS. Your workspace is ready, and full access has been enabled for your account.
                </>
              ) : (
                <>
                  <span className="font-semibold text-[#334155]">{companyName}</span> is currently completing the initial organization setup on HRMS. Your account is active, and full access will be enabled once your company administrator finishes workspace configuration.
                </>
              )}
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0 sm:pt-1">
            {isCompleted ? (
              <button
                onClick={() => navigate("/")}
                className="inline-flex items-center justify-center gap-2 bg-[#0052ea] hover:bg-[#0043c2] text-white font-medium text-[14px] px-5 py-2.5 rounded-[8px] transition shadow-sm cursor-pointer min-w-[155px] h-[42px] select-none"
              >
                <span>Go to Dashboard</span>
                <MdArrowForward className="text-[18px] shrink-0" />
              </button>
            ) : (
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="inline-flex items-center justify-center gap-2 bg-[#0052ea] hover:bg-[#0043c2] text-white font-medium text-[14px] px-4 py-2.5 rounded-[8px] transition shadow-sm cursor-pointer disabled:opacity-70 min-w-[145px] h-[42px] select-none"
              >
                <MdRefresh className={`text-[18px] shrink-0 ${isRefreshing ? "animate-spin" : ""}`} />
                <span className="whitespace-nowrap">{isRefreshing ? "Checking..." : "Check Status"}</span>
              </button>
            )}
            <button
              onClick={handleLogout}
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 border border-[#cbd5e1] text-[#475569] font-medium text-[14px] px-4 py-2.5 rounded-[8px] transition cursor-pointer h-[42px] select-none"
            >
              <MdLogout className="text-[18px] text-[#64748b] shrink-0" />
              <span className="whitespace-nowrap">Sign Out</span>
            </button>
          </div>
        </div>

        {/* Dynamic Body Content */}
        {isCompleted ? (
          <div className="mt-8 bg-[#f0fdf4] border border-[#bbf7d0] rounded-[12px] p-5 flex items-start gap-4 shadow-2xs">
            <div className="bg-[#dcfce7] rounded-full p-2.5 shrink-0 mt-0.5">
              <MdCheckCircle className="text-[#16a34a] text-[26px]" />
            </div>
            <div className="flex flex-col gap-1">
              <h2 className="text-[17px] font-bold text-[#14532d] m-0">
                Organization Setup Completed!
              </h2>
              <p className="text-[14px] text-[#166534] leading-[22px] m-0">
                Your company administrator has finished configuring your organization's workspace. You now have full access to mark attendance, apply for leaves, and view your employee profile.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Contact Administrator Notice */}
            <div className="mt-8 bg-[#fffbeb] border border-[#fde68a] rounded-[12px] p-4 flex items-start gap-3.5">
              <MdInfoOutline className="text-[#d97706] text-[22px] shrink-0 mt-0.5" />
              <div className="flex flex-col gap-0.5 text-sm text-[#92400e] leading-relaxed">
                <span className="font-semibold text-[#78350f]">Setup In Progress</span>
                <span>
                  Please reach out to your Company Administrator to finalize the organization setup and activate full workspace access for your account.
                </span>
              </div>
            </div>

            {/* Informative Guidance Banner */}
            <div className="mt-4 bg-[#f0fdf4] border border-[#bbf7d0] rounded-[12px] p-4 flex items-start gap-3.5">
              <MdInfoOutline className="text-[#16a34a] text-[22px] shrink-0 mt-0.5" />
              <div className="text-sm text-[#166534] leading-relaxed">
                <span className="font-semibold">Next steps:</span> You do not need to take any action right now. Once your Company Administrator completes the organization setup, you will be able to mark attendance, request leaves, and view your employee profile.
              </div>
            </div>
          </>
        )}

      </div>

      {/* Access Restricted Popup Modal */}
      <Dialog 
        open={showRestrictedModal} 
        onClose={handleCloseRestrictedModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: { borderRadius: "16px", padding: "8px" }
        }}
      >
        <div className="p-6 flex flex-col items-center text-center">
          <div className="bg-[#fef2f2] border border-[#fee2e2] rounded-full p-4 mb-4">
            <MdLockOutline className="text-[#dc2626] text-[36px]" />
          </div>
          <h2 className="text-[#0f172a] font-bold text-[22px] mb-2">
            Access Restricted
          </h2>
          <p className="text-[#64748b] text-[14px] leading-[22px] mb-6">
            You tried to access a restricted page. Your organization onboarding setup is currently in progress by your Company Administrator.
          </p>
          <button
            onClick={handleCloseRestrictedModal}
            className="bg-[#0052ea] hover:bg-[#0043c2] text-white font-semibold text-[15px] py-2.5 px-8 rounded-[8px] transition-colors cursor-pointer w-full"
          >
            Understood
          </button>
        </div>
      </Dialog>
    </div>
  );
}
